import _ from 'lodash';
import { AnyAction } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import {
    ILoanDrawdownConditionDeleteAction,
    ILoanDrawdownConditionDocumentDeleteAction,
    ILoanDrawdownConditionDocumentSetAction,
    ILoanDrawdownConditionDocumentsSetAction,
    ILoanDrawdownConditionSetAction,
    ILoanDrawdownConditionsSetAction,
} from './actions';
import LoanDrawdownsActionsEnum from './ActionsEnum';
import ILoanDrawdownCondition from '~Api/LoanDrawdowns/ILoanDrawdownCondition';
import ILoanDrawdownConditionDocument from '~Api/LoanDrawdowns/ILoanDrawdownConditionDocument';

export interface ILoanDrawdownsState {
    drawdownConditionDocuments: IDictionary<ILoanDrawdownConditionDocument>;
    drawdownConditions: IDictionary<ILoanDrawdownCondition>;
    loanDrawdownConditionDocumentUuids: IDictionary<string[]>;
    loanDrawdownConditionDocumentsListed: IDictionary<boolean>;
    loanDrawdownConditionUuids: IDictionary<string[]>;
    loanDrawdownConditionsListed: IDictionary<boolean>;
}

const initialData: ILoanDrawdownsState = {
    drawdownConditionDocuments: {},
    drawdownConditions: {},
    loanDrawdownConditionDocumentUuids: {},
    loanDrawdownConditionDocumentsListed: {},
    loanDrawdownConditionUuids: {},
    loanDrawdownConditionsListed: {},
};

export function loanDrawdownsReducer(state: ILoanDrawdownsState = initialData, action: AnyAction): ILoanDrawdownsState {
    switch (action.type) {
        case LoanDrawdownsActionsEnum.LoanDrawdownConditionSet: {
            const typedAction: ILoanDrawdownConditionSetAction = action as ILoanDrawdownConditionSetAction;

            return {
                ...state,
                drawdownConditions: {
                    ...state.drawdownConditions,
                    [typedAction.drawdownCondition.uuid]: typedAction.drawdownCondition,
                },
                loanDrawdownConditionUuids: {
                    ...state.loanDrawdownConditionUuids,
                    [typedAction.drawdownCondition.loanDrawdownUuid]: _.uniq([
                        ...(state.loanDrawdownConditionUuids[typedAction.drawdownCondition.loanDrawdownUuid] || []),
                        typedAction.drawdownCondition.uuid,
                    ]),
                },
            };
        }

        case LoanDrawdownsActionsEnum.LoanDrawdownConditionsSet: {
            const typedAction: ILoanDrawdownConditionsSetAction = action as ILoanDrawdownConditionsSetAction;

            return {
                ...state,
                drawdownConditions: {
                    ...state.drawdownConditions,
                    ..._.keyBy(typedAction.drawdownConditions, 'uuid'),
                },
                loanDrawdownConditionUuids: {
                    ...state.loanDrawdownConditionUuids,
                    [typedAction.drawdownUuid]: _.map(typedAction.drawdownConditions, 'uuid'),
                },
                loanDrawdownConditionsListed: {
                    [typedAction.drawdownUuid]: true,
                },
            };
        }

        case LoanDrawdownsActionsEnum.LoanDrawdownConditionDelete: {
            const typedAction: ILoanDrawdownConditionDeleteAction = action as ILoanDrawdownConditionDeleteAction;
            const loanDrawdownUuid: string = state.drawdownConditions[typedAction.loanDrawdownConditionUuid].loanDrawdownUuid;

            return {
                ...state,
                drawdownConditions: _.omit(state.drawdownConditions, typedAction.loanDrawdownConditionUuid),
                loanDrawdownConditionUuids: {
                    ...state.loanDrawdownConditionUuids,
                    [loanDrawdownUuid]: _.without(state.loanDrawdownConditionUuids[loanDrawdownUuid], typedAction.loanDrawdownConditionUuid),
                },
            };
        }

        case LoanDrawdownsActionsEnum.LoanDrawdownConditionDocumentSet: {
            const typedAction: ILoanDrawdownConditionDocumentSetAction = action as ILoanDrawdownConditionDocumentSetAction;

            return {
                ...state,
                drawdownConditionDocuments: {
                    ...state.drawdownConditionDocuments,
                    [typedAction.drawdownConditionDocument.uuid]: typedAction.drawdownConditionDocument,
                },
                loanDrawdownConditionDocumentUuids: {
                    ...state.loanDrawdownConditionDocumentUuids,
                    [typedAction.drawdownConditionDocument.loanDrawdownConditionUuid]: _.uniq([
                        ...(state.loanDrawdownConditionDocumentUuids[typedAction.drawdownConditionDocument.loanDrawdownConditionUuid] || []),
                        typedAction.drawdownConditionDocument.uuid,
                    ]),
                },
            };
        }

        case LoanDrawdownsActionsEnum.LoanDrawdownConditionDocumentsSet: {
            const typedAction: ILoanDrawdownConditionDocumentsSetAction = action as ILoanDrawdownConditionDocumentsSetAction;

            const loanDrawdownConditionDocumentUuids: IDictionary<string[]> = {};
            const drawdownConditionDocuments: IDictionary<ILoanDrawdownConditionDocument> = {};
            _.each(typedAction.drawdownConditionDocuments, (loanDrawdownConditionDocument: ILoanDrawdownConditionDocument) => {
                if (!loanDrawdownConditionDocumentUuids[loanDrawdownConditionDocument.loanDrawdownConditionUuid]) {
                    loanDrawdownConditionDocumentUuids[loanDrawdownConditionDocument.loanDrawdownConditionUuid] = [];
                }

                drawdownConditionDocuments[loanDrawdownConditionDocument.uuid] = loanDrawdownConditionDocument;
                loanDrawdownConditionDocumentUuids[loanDrawdownConditionDocument.loanDrawdownConditionUuid].push(loanDrawdownConditionDocument.uuid);
            });

            return {
                ...state,
                drawdownConditionDocuments: {
                    ...state.drawdownConditionDocuments,
                    ...drawdownConditionDocuments,
                },
                loanDrawdownConditionDocumentUuids: {
                    ...state.loanDrawdownConditionDocumentUuids,
                    ...loanDrawdownConditionDocumentUuids,
                },
                loanDrawdownConditionDocumentsListed: {
                    [typedAction.drawdownUuid]: true,
                },
            };
        }

        case LoanDrawdownsActionsEnum.LoanDrawdownConditionDocumentDelete: {
            const typedAction: ILoanDrawdownConditionDocumentDeleteAction = action as ILoanDrawdownConditionDocumentDeleteAction;
            const loanDrawdownConditionUuid: string = state.drawdownConditionDocuments[typedAction.loanDrawdownConditionDocumentUuid].loanDrawdownConditionUuid;

            return {
                ...state,
                drawdownConditionDocuments: _.omit(state.drawdownConditionDocuments, typedAction.loanDrawdownConditionDocumentUuid),
                loanDrawdownConditionDocumentUuids: {
                    ...state.loanDrawdownConditionDocumentUuids,
                    [loanDrawdownConditionUuid]: _.without(state.loanDrawdownConditionDocumentUuids[loanDrawdownConditionUuid], typedAction.loanDrawdownConditionDocumentUuid),
                },
            };
        }

        default:
            return state;
    }
}
